import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AtSign, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { CustomerToolOption } from './ToolSelector';
import {
    activeMentionQuery,
    findMentionedTools,
    insertMention,
    matchMentionCandidates,
    removeMention,
} from './toolMentionText';

/**
 * **Werkzeuge per @ im Chat-Eingabefeld.** `@` öffnet die Liste der eigenen Werkzeuge, gefiltert
 * beim Weitertippen; ⭡/⭣ wählt, Enter oder Tab übernimmt, Escape schließt. Übernommen wird als
 * `@Name ` in den Text — der Text bleibt die Wahrheit (siehe `toolMentionText.ts`), die Chips über
 * dem Feld zeigen, was beim Absenden mitgeht.
 */
export function useToolMentions({
    tools,
    value,
    onValueChange,
    fieldRef,
    enabled,
}: {
    tools: CustomerToolOption[];
    value: string;
    onValueChange: (value: string) => void;
    fieldRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement | null>;
    enabled: boolean;
}) {
    const [caret, setCaret] = useState<number | null>(null);
    const [highlight, setHighlight] = useState(0);
    // Escape schließt die Liste für genau dieses `@`; ein neues öffnet sie wieder.
    const [dismissedAt, setDismissedAt] = useState<number | null>(null);

    const active = enabled && caret !== null ? activeMentionQuery(value, caret) : null;
    const candidates = useMemo(
        () => (active && active.start !== dismissedAt ? matchMentionCandidates(active.query, tools) : []),
        [active?.start, active?.query, dismissedAt, tools], // eslint-disable-line react-hooks/exhaustive-deps
    );
    const open = candidates.length > 0;

    useEffect(() => {
        setHighlight(0);
    }, [active?.start, active?.query]);

    const syncCaret = useCallback(() => {
        const field = fieldRef.current;
        setCaret(field ? field.selectionStart : null);
    }, [fieldRef]);

    const choose = useCallback(
        (tool: CustomerToolOption) => {
            if (!active || caret === null) return;
            const next = insertMention(value, active, caret, tool.name);
            onValueChange(next.text);
            setCaret(next.caret);
            requestAnimationFrame(() => {
                const field = fieldRef.current;
                if (!field) return;
                field.focus();
                field.setSelectionRange(next.caret, next.caret);
            });
        },
        [active, caret, fieldRef, onValueChange, value],
    );

    /** Gibt `true` zurück, wenn die Taste der Liste galt — dann sendet das Feld nicht. */
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent): boolean => {
            if (!open) return false;
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                const step = event.key === 'ArrowDown' ? 1 : -1;
                setHighlight((current) => (current + step + candidates.length) % candidates.length);
            } else if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') {
                choose(candidates[Math.min(highlight, candidates.length - 1)]);
            } else if (event.key === 'Escape') {
                setDismissedAt(active?.start ?? null);
            } else {
                return false;
            }
            event.preventDefault();
            return true;
        },
        [active?.start, candidates, choose, highlight, open],
    );

    const mentioned = useMemo(() => (enabled ? findMentionedTools(value, tools) : []), [enabled, tools, value]);
    const remove = useCallback(
        (tool: CustomerToolOption) => onValueChange(removeMention(value, tool.name)),
        [onValueChange, value],
    );

    return { open, candidates, highlight, setHighlight, choose, handleKeyDown, syncCaret, mentioned, remove };
}

export function ToolMentionMenu({
    candidates,
    highlight,
    onChoose,
    onHighlight,
}: {
    candidates: CustomerToolOption[];
    highlight: number;
    onChoose: (tool: CustomerToolOption) => void;
    onHighlight: (index: number) => void;
}) {
    const { t } = useTranslation();
    if (!candidates.length) return null;
    return (
        <div
            role="listbox"
            data-testid="chat-tool-mention-menu"
            aria-label={t('chatExchange.toolMentions.menuLabel')}
            className="absolute bottom-full left-0 z-[1000] mb-1 max-h-[280px] w-full max-w-[420px] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
        >
            {candidates.map((tool, index) => (
                <button
                    key={tool.id}
                    type="button"
                    role="option"
                    aria-selected={index === highlight}
                    data-testid="chat-tool-mention-option"
                    className={cn(
                        'flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent',
                        index === highlight && 'bg-accent',
                    )}
                    // Der Fokus bleibt im Feld, sonst wandert die Schreibmarke.
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => onHighlight(index)}
                    onClick={() => onChoose(tool)}
                >
                    <AtSign className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="min-w-0 flex-1 overflow-hidden">
                        <span className="block truncate" title={tool.name}>
                            {tool.name}
                        </span>
                        {tool.description && (
                            <span className="block truncate text-xs text-muted-foreground" title={tool.description}>
                                {tool.description}
                            </span>
                        )}
                    </span>
                </button>
            ))}
        </div>
    );
}

export function ToolMentionChips({
    tools,
    onRemove,
}: {
    tools: CustomerToolOption[];
    onRemove: (tool: CustomerToolOption) => void;
}) {
    const { t } = useTranslation();
    if (!tools.length) return null;
    return (
        <div className="flex flex-wrap gap-1 px-1 pb-1" data-testid="chat-tool-mention-chips">
            {tools.map((tool) => (
                <span
                    key={tool.id}
                    data-testid="chat-tool-mention-chip"
                    className="inline-flex max-w-[280px] items-center gap-1 rounded-full bg-[var(--oa-surface)] py-0.5 pl-2 pr-1 text-xs text-[var(--oa-fg)]"
                >
                    <AtSign className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="truncate" title={tool.name}>
                        {tool.name}
                    </span>
                    <button
                        type="button"
                        className="rounded-full p-0.5 hover:bg-accent"
                        aria-label={t('chatExchange.toolMentions.remove', { name: tool.name })}
                        onClick={() => onRemove(tool)}
                    >
                        <X className="h-3 w-3" aria-hidden />
                    </button>
                </span>
            ))}
        </div>
    );
}
